import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { firestore } from '../../config/firebase';
import { parseAdminUidList, resolveAdminShellAccess } from '../../constants/adminRoles';

/**
 * LEGACY_ADMIN_ALLOWLIST: env ∪ appConfig/feedback.adminUids if custom claim is missing.
 * Env is a fallback, not an exclusive override that ignores Firestore.
 */
async function loadLegacyUidSet() {
  const set = parseAdminUidList(import.meta.env.VITE_FEEDBACK_ADMIN_UIDS || '');
  try {
    const snap = await getDoc(doc(firestore, 'appConfig', 'feedback'));
    const uids = snap.exists() ? snap.data()?.adminUids || [] : [];
    if (Array.isArray(uids)) {
      for (const u of uids) if (typeof u === 'string' && u) set.add(u);
    }
  } catch (e) {
    console.error('AdminRoute legacy allowlist read error:', e);
  }
  return set;
}

export default function AdminRoute({ children }) {
  const { currentUser, loading, adminRole, adminRoleResolved, impersonation } = useAuth();
  const location = useLocation();
  const [legacyUidSet, setLegacyUidSet] = React.useState(null);

  const impersonationActive = Boolean(impersonation?.active);
  const needsLegacy = Boolean(
    currentUser && adminRoleResolved && !impersonationActive && !adminRole,
  );

  React.useEffect(() => {
    if (!needsLegacy) return undefined;
    let mounted = true;
    loadLegacyUidSet().then((set) => {
      if (mounted) setLegacyUidSet(set);
    });
    return () => {
      mounted = false;
    };
  }, [needsLegacy, currentUser?.uid]);

  if (loading || !adminRoleResolved) return null;
  if (!currentUser) return <Navigate to={`/login${location.search}`} replace />;
  if (impersonationActive) return <Navigate to="/" replace />;
  if (needsLegacy && legacyUidSet == null) return null;

  const allowed = resolveAdminShellAccess({
    adminRole,
    impersonationActive: false,
    uid: currentUser.uid,
    legacyUidSet: legacyUidSet || new Set(),
  });
  return allowed ? children : <Navigate to="/" replace />;
}
